import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ShieldCheck, Lock, CheckCircle2, Loader2, Coins, Building2, TrendingDown, Calculator, FileText, Instagram, MessageCircle, CalendarClock, Quote, EyeOff, Handshake, Landmark, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "393497173896";
const GUIDA_ORO_PDF = "https://customer-assets-rejwkqb3.emergentagent.net/job_viral-wealth-watch/artifacts/2wgwnaee_Guida_Oro_Fisico_Alex_Gold%281%29.pdf";

// ── Google Ads / GA4 conversion tracking ──────────────────────────────
// Incolla qui il tuo ID conversione Google Ads (formato "AW-XXXXXXXXX")
// e l'etichetta conversione (label). Lasciali vuoti finché non li hai:
// l'evento viene comunque inviato al dataLayer (compatibile con GTM).
const GOOGLE_ADS_ID = "";
const GOOGLE_ADS_CONVERSION_LABEL = "";
// Se usi un calendario esterno (Calendly / Google Calendar appointment), incolla qui l'URL.
// Se vuoto, il lead sceglie giorno + fascia e conferma via WhatsApp.
const CALENDLY_URL = "";

function loadGtag() {
  if (!GOOGLE_ADS_ID || window.gtag) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ADS_ID);
}

function fireLeadConversion() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "generate_lead", form: "oro_landing", source: "google_ads" });
  if (window.gtag && GOOGLE_ADS_ID && GOOGLE_ADS_CONVERSION_LABEL) {
    window.gtag("event", "conversion", { send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}` });
  }
}

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1b66fcb6-686c-496a-809e-79f0cfe50fdc/images/f29d6e160506b998dab8515bae28503ddc5da6ac5bb79621a3a11e12382444d1.jpeg";

const PILLARS = [
  {
    icon: TrendingDown,
    img: "https://static.prod-images.emergentagent.com/jobs/1b66fcb6-686c-496a-809e-79f0cfe50fdc/images/2cc1334c231d970b3deafea2e784ee576b3d0231ea56765297990f8e9746b7db.jpeg",
    title: "La liquidità ferma perde valore",
    text: "Con l'inflazione, i soldi lasciati sul conto perdono potere d'acquisto ogni anno. È un'erosione silenziosa, non un'opinione.",
  },
  {
    icon: Coins,
    img: "https://static.prod-images.emergentagent.com/jobs/1b66fcb6-686c-496a-809e-79f0cfe50fdc/images/7850d0ea6d80ea770854679019d3d984540bfb9a933ba0d0b04f26d71a338302.jpeg",
    title: "Oro fisico, un bene tangibile",
    text: "Un asset reale che possiedi davvero, fuori dal sistema bancario. Non un prodotto finanziario: un bene rifugio che attraversa le crisi.",
  },
  {
    icon: Building2,
    img: "https://static.prod-images.emergentagent.com/jobs/1b66fcb6-686c-496a-809e-79f0cfe50fdc/images/99514631465d87970141f4b7e93fbc0c6bc42b26f877edc3b82a294f66449906.jpeg",
    title: "Pensato per chi ha una SRL",
    text: "Imprenditori, famiglie e professionisti con liquidità idle: un percorso di diversificazione su misura per il tuo caso, con trasparenza sui costi.",
  },
];

export default function OroLanding() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", privacy: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [slotGiorno, setSlotGiorno] = useState("");
  const [slotFascia, setSlotFascia] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideDone, setGuideDone] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideForm, setGuideForm] = useState({ nome: "", cognome: "", email: "", privacy: false });

  useEffect(() => { loadGtag(); }, []);

  // Provenienza dinamica dai parametri UTM (?utm_source=instagram&utm_medium=bio ...)
  const provenienza = (() => {
    const p = new URLSearchParams(window.location.search);
    const src = (p.get("utm_source") || "").trim();
    const med = (p.get("utm_medium") || "").trim();
    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    if (src) return med ? `${cap(src)} · ${med}` : cap(src);
    if (p.get("gclid")) return "Google Ads";
    return "Diretto";
  })();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const goToForm = () => {
    const el = document.getElementById("oro-consulenza");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openGuide = () => { setGuideDone(false); setGuideOpen(true); };
  const closeGuide = () => setGuideOpen(false);
  const setGuide = (k) => (e) => setGuideForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submitGuide = async (e) => {
    e.preventDefault();
    const { nome, cognome, email, privacy } = guideForm;
    if (!nome.trim() || !cognome.trim() || !email.trim()) { toast.error("Compila nome, cognome ed email."); return; }
    if (!privacy) { toast.error("Devi accettare l'informativa privacy."); return; }
    setGuideLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        tipo: "cliente",
        nome: `${nome.trim()} ${cognome.trim()}`,
        email: email.trim(),
        telefono: "",
        provenienza,
        messaggio: "[Landing /oro] Ha richiesto la Guida gratuita all'oro fisico.",
      });
      fireLeadConversion();
      setGuideDone(true);
      setTimeout(() => window.open(GUIDA_ORO_PDF, "_blank"), 400);
      toast.success("Guida pronta! Ti ho inviato una copia via email 📩");
    } catch {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally { setGuideLoading(false); }
  };

  const giorniOpts = (() => {
    const out = [];
    const wd = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
    const d0 = new Date();
    for (let i = 1; i <= 7; i++) {
      const d = new Date(d0); d.setDate(d0.getDate() + i);
      out.push({ v: `${wd[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`, });
    }
    return out;
  })();
  const fasce = ["Mattina (9–13)", "Pomeriggio (14–18)", "Sera (18–20)"];

  const slotText = slotGiorno || slotFascia
    ? ` Preferirei ${slotGiorno ? `${slotGiorno}` : ""}${slotFascia ? `, ${slotFascia.toLowerCase()}` : ""}.`
    : "";
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Ciao Alex, ho compilato il form sul sito e vorrei fissare una call riservata sulla protezione della mia liquidità.${slotText}`)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.telefono.trim()) {
      toast.error("Compila nome, email e telefono."); return;
    }
    if (!form.privacy) { toast.error("Devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        tipo: "cliente",
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        provenienza,
        messaggio: "[Landing /oro] Richiesta di essere ricontattato per una call conoscitiva riservata (protezione patrimonio con oro fisico).",
      });
      fireLeadConversion();
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatto io.");
    } catch {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-xl px-4 py-3 text-[15px] outline-none border focus:border-[#C9A227]";
  const inputStyle = { background: "#211f1c", borderColor: "#3a3733", color: "#EDE7DB" };

  return (
    <div className="min-h-screen" style={{ background: "#EDE7DB", color: "#161513", fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="oro-landing-page">
      <Toaster theme="light" position="top-center" />

      {/* Top bar */}
      <header className="border-b" style={{ borderColor: "#161513" }}>
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <span className="font-bold tracking-tight text-lg whitespace-nowrap">ALEX GOLD</span>
          <span className="hidden sm:block text-[11px] uppercase tracking-[0.25em] text-right" style={{ color: "#6E1F2A" }}>Oro &amp; Protezione Patrimoniale</span>
        </div>
      </header>

      {/* Hero (Partite IVA) — ancora #partite-iva per link in bio */}
      <section className="relative" id="partite-iva" data-testid="oro-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(22,21,19,0.92) 0%, rgba(22,21,19,0.80) 45%, rgba(22,21,19,0.55) 100%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 py-16 md:py-24" style={{ color: "#EDE7DB" }}>
          <div className="max-w-2xl">
            <span className="inline-block text-[11px] uppercase tracking-[0.25em] px-3 py-1 rounded-full mb-5" style={{ background: "#6E1F2A", color: "#EDE7DB" }}>
              Per Partite IVA
            </span>
            <h1 className="font-bold leading-[1.05] text-4xl sm:text-5xl mb-5">Nessuno accantona per te.</h1>
            <p className="text-lg leading-relaxed mb-6" style={{ color: "#d8d2c6" }}>
              Se hai la partita IVA, nessuno mette da parte una liquidazione per te. Costruisci tu la tua
              <b> riserva in oro fisico</b>, ai tuoi tempi, fuori dal sistema bancario.
            </p>
            <ul className="space-y-2.5">
              {["Nessun obbligo, nessuna promessa di rendimento", "Un percorso su misura per il tuo caso", "100% riservato"].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5 text-base">
                  <CheckCircle2 size={18} style={{ color: "#C9A227" }} /> {t}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-7" data-testid="oro-hero-ctas">
              <a href="/calcolatore" target="_blank" rel="noopener noreferrer" data-testid="oro-hero-calcolatore" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5" style={{ background: "#C9A227", color: "#161513" }}>
                <Calculator size={17} /> Calcola quanto perdi (gratis)
              </a>
              <a href="/guida" target="_blank" rel="noopener noreferrer" data-testid="oro-hero-guida" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold border" style={{ borderColor: "#EDE7DB", color: "#EDE7DB" }}>
                <FileText size={17} /> Scarica la guida (gratis)
              </a>
              <button type="button" onClick={goToForm} data-testid="oro-hero-consulenza" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold border transition-transform hover:-translate-y-0.5" style={{ borderColor: "#EDE7DB", color: "#EDE7DB" }}>
                Prenota la consulenza gratuita <span aria-hidden>→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TFR / Liquidazione per partita IVA (banda scura) */}
      <section className="py-16" style={{ background: "#161513", color: "#EDE7DB" }} data-testid="oro-tfr-section">
        <div className="max-w-5xl mx-auto px-5">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-3 py-1 rounded-full mb-5" style={{ background: "#6E1F2A", color: "#EDE7DB" }}>
              <Landmark size={13} /> Consulenza patrimoniale
            </span>
            <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl mb-5">Il tuo TFR non esiste. Costruisci la tua riserva.</h2>
            <p className="text-lg leading-relaxed mb-7" style={{ color: "#d8d2c6" }}>
              Se sei dipendente, ogni mese qualcun altro accumula una liquidazione per te. Se hai la partita IVA,
              quella riserva non esiste — a meno che non te la costruisca tu.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Nessun accumulo automatico: la riserva la decidi tu.",
                "Puoi costruirti una riserva tua, in oro fisico, ai tuoi tempi.",
                "Costruisci la tua riserva, ai tuoi tempi, fuori dalle banche",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-base" data-testid={`oro-tfr-check-${i}`}>
                  <CheckCircle2 size={19} className="mt-0.5 shrink-0" style={{ color: "#C9A227" }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={goToForm} data-testid="oro-tfr-cta"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: "#C9A227", color: "#161513" }}>
              Prenota la consulenza gratuita <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Proteggi ciò che hai costruito — imprenditori con SRL (spostato sotto il nuovo hero) */}
      <section className="py-16" style={{ background: "#161513", color: "#EDE7DB", borderTop: "1px solid #3a3733" }} data-testid="oro-srl-section">
        <div className="max-w-5xl mx-auto px-5">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-3 py-1 rounded-full mb-5" style={{ background: "#6E1F2A", color: "#EDE7DB" }}>
              <Building2 size={13} /> Per imprenditori con SRL
            </span>
            <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl mb-5">Proteggi ciò che hai costruito.</h2>
            <p className="text-lg leading-relaxed mb-7" style={{ color: "#d8d2c6" }}>
              Scopri come professionisti, famiglie e imprenditori con una SRL diversificano la <b>liquidità ferma</b> con
              <b> oro fisico reale</b>, fuori dal sistema bancario. Nessuna promessa di rendimento — solo un bene tangibile.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "100% riservato, senza esporti",
                "Nessun obbligo, nessuna promessa di rendimento",
                "Un percorso su misura per il tuo caso",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-base" data-testid={`oro-srl-check-${i}`}>
                  <CheckCircle2 size={19} className="mt-0.5 shrink-0" style={{ color: "#C9A227" }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={goToForm} data-testid="oro-srl-cta"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: "#C9A227", color: "#161513" }}>
              Prenota la consulenza gratuita <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-5">
        {/* Vuoi prima farti un'idea? (spostato subito dopo l'hero) */}
        <section className="py-14" data-testid="oro-secondary-ctas">
          <div className="rounded-2xl p-7 text-center" style={{ background: "#161513", color: "#EDE7DB" }}>
            <ShieldCheck size={28} className="mx-auto mb-3" style={{ color: "#C9A227" }} />
            <h3 className="font-bold text-2xl mb-2">Vuoi prima farti un'idea?</h3>
            <p className="text-sm mb-6" style={{ color: "#c9c3b6" }}>Nessuna pressione. Esplora gli strumenti gratuiti e decidi con calma.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/calcolatore" data-testid="oro-cta-calcolatore" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold" style={{ background: "#EDE7DB", color: "#161513" }}>
                <Calculator size={16} /> Calcolatore gratuito
              </Link>
              <button type="button" onClick={openGuide} data-testid="oro-cta-guida" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold border" style={{ borderColor: "#EDE7DB", color: "#EDE7DB" }}>
                <FileText size={16} /> Guida gratuita
              </button>
              <a href="https://instagram.com/alexgold.oro" target="_blank" rel="noopener noreferrer" data-testid="oro-cta-instagram" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold border" style={{ borderColor: "#EDE7DB", color: "#EDE7DB" }}>
                <Instagram size={16} /> @alexgold.oro
              </a>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="py-14">
          <div className="grid sm:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="rounded-2xl overflow-hidden border" style={{ borderColor: "#161513", background: "#f5f1e8" }} data-testid={`oro-pillar-${i}`}>
                  <img src={p.img} alt="" className="w-full h-36 object-cover" />
                  <div className="p-6">
                    <div className="inline-grid place-items-center w-11 h-11 rounded-xl mb-4 -mt-11 relative border-2" style={{ background: "#6E1F2A", color: "#EDE7DB", borderColor: "#f5f1e8" }}>
                      <Icon size={20} />
                    </div>
                    <h3 className="font-bold text-lg leading-tight mb-2">{p.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "#4a473f" }}>{p.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Prova sociale */}
        <section className="pb-14" data-testid="oro-social-proof">
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: EyeOff, t: "100% riservato", s: "Nessuna esposizione pubblica: si parla in privato, solo tu ed io." },
              { icon: ShieldCheck, t: "Trasparenza sui costi", s: "Premio, spread e liquidabilità alla luce del sole, prima di ogni scelta." },
              { icon: Handshake, t: "Nessuna promessa", s: "Nessuna promessa di rendimento: informazione chiara, decisione tua." },
            ].map((x, i) => {
              const Icon = x.icon;
              return (
                <div key={i} className="rounded-xl p-5 border text-left" style={{ borderColor: "#161513", background: "#f5f1e8" }} data-testid={`oro-trust-${i}`}>
                  <Icon size={22} style={{ color: "#6E1F2A" }} className="mb-3" />
                  <h4 className="font-bold text-base mb-1">{x.t}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: "#4a473f" }}>{x.s}</p>
                </div>
              );
            })}
          </div>
          <h3 className="text-center font-bold text-2xl mb-6">La parola ai miei clienti</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { q: "Alex si è dimostrato estremamente serio e professionale. La sua profonda conoscenza dell'oro offre una prospettiva solida e sicura: un'opportunità concreta per proteggere i propri risparmi. Lo consiglio vivamente a chi cerca stabilità nel lungo termine.", a: "Marco", r: "Imprenditore con SRL" },
              { q: "Sempre disponibile e professionale, si distingue per gentilezza e affidabilità: lavora con impegno e passione. Consigliato per chi cerca qualità e serietà.", a: "Roberto", r: "Libero professionista" },
              { q: "Si capisce subito che sei un professionista. Competenza e serietà fuori dal comune.", a: "Alessandro", r: "Cliente", intro: "Non sei il solo a chiederti se ha senso." },
            ].map((t, i) => (
              <div key={i} className="rounded-xl p-5 border" style={{ borderColor: "#161513", background: "#161513", color: "#EDE7DB" }} data-testid={`oro-testimonial-${i}`}>
                {t.intro && <p className="text-sm italic mb-2" style={{ color: "#C9A227" }}>{t.intro}</p>}
                <Quote size={20} style={{ color: "#C9A227" }} className="mb-2" />
                <p className="text-sm leading-relaxed mb-4" style={{ color: "#d8d2c6" }}>"{t.q}"</p>
                <p className="text-sm font-bold">{t.a}</p>
                <p className="text-[12px]" style={{ color: "#8a857a" }}>{t.r}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Form "Prenota una call riservata" (spostato in fondo) */}
        <section className="pb-16" id="oro-consulenza" data-testid="oro-form-section">
          <p className="text-center text-lg mb-5" style={{ color: "#4a473f" }}>Hai già le idee più chiare, o vuoi solo parlarne?</p>
          <div className="max-w-md mx-auto rounded-2xl p-6 sm:p-7 shadow-2xl border" style={{ background: "#161513", color: "#EDE7DB", borderColor: "#3a3733" }} data-testid="oro-form-card">
            {done ? (
              <div className="text-center py-6" data-testid="oro-form-success">
                <div className="inline-grid place-items-center w-16 h-16 rounded-full mb-4" style={{ background: "#6E1F2A" }}>
                  <CheckCircle2 size={30} />
                </div>
                <h3 className="font-bold text-xl mb-2">Richiesta ricevuta 🙌</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "#c9c3b6" }}>
                  Grazie {form.nome.split(" ")[0]}. Fissiamo una <b>call conoscitiva riservata</b>: scegli quando preferisci essere ricontattato.
                </p>
                {CALENDLY_URL ? (
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="oro-success-calendar"
                    className="flex items-center justify-center gap-2 w-full rounded-full py-3.5 text-sm font-semibold mb-2.5" style={{ background: "#C9A227", color: "#161513" }}>
                    <CalendarClock size={17} /> Prenota uno slot nel calendario
                  </a>
                ) : (
                  <div className="text-left mb-3" data-testid="oro-slot-picker">
                    <p className="text-[11px] uppercase tracking-[0.15em] mb-1.5" style={{ color: "#8a857a" }}>Giorno preferito</p>
                    <select value={slotGiorno} onChange={(e) => setSlotGiorno(e.target.value)} data-testid="oro-slot-giorno"
                      className="w-full rounded-xl px-3 py-2.5 text-sm mb-3 outline-none border" style={{ background: "#211f1c", borderColor: "#3a3733", color: "#EDE7DB" }}>
                      <option value="">Nessuna preferenza</option>
                      {giorniOpts.map((g, i) => <option key={i} value={g.v}>{g.v}</option>)}
                    </select>
                    <p className="text-[11px] uppercase tracking-[0.15em] mb-1.5" style={{ color: "#8a857a" }}>Fascia oraria</p>
                    <div className="flex flex-wrap gap-2">
                      {fasce.map((f) => (
                        <button key={f} type="button" onClick={() => setSlotFascia((v) => v === f ? "" : f)}
                          data-testid={`oro-slot-fascia-${fasce.indexOf(f)}`}
                          className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                          style={slotFascia === f ? { background: "#6E1F2A", borderColor: "#6E1F2A", color: "#EDE7DB" } : { background: "transparent", borderColor: "#3a3733", color: "#c9c3b6" }}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="oro-success-whatsapp"
                  className="flex items-center justify-center gap-2 w-full rounded-full py-3.5 text-sm font-semibold mb-2.5" style={{ background: "#25D366", color: "#04311a" }}>
                  <MessageCircle size={17} /> Conferma su WhatsApp
                </a>
                <Link to="/guida" data-testid="oro-success-guida"
                  className="block w-full rounded-full py-3 text-sm font-semibold border" style={{ borderColor: "#EDE7DB", color: "#EDE7DB" }}>
                  📘 Intanto scarica la Guida gratuita
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-bold text-2xl leading-tight mb-1">Prenota una call riservata</h2>
                <p className="text-sm mb-5" style={{ color: "#c9c3b6" }}>Lascia i tuoi dati: ti ricontatto io per fissarla. 100% riservato.</p>
                <form onSubmit={submit} className="space-y-3" data-testid="oro-lead-form">
                  <input value={form.nome} onChange={set("nome")} placeholder="Nome e cognome" data-testid="oro-input-nome" className={inputCls} style={inputStyle} />
                  <input value={form.email} onChange={set("email")} type="email" placeholder="Email" data-testid="oro-input-email" className={inputCls} style={inputStyle} />
                  <input value={form.telefono} onChange={set("telefono")} type="tel" placeholder="Telefono (WhatsApp)" data-testid="oro-input-telefono" className={inputCls} style={inputStyle} />
                  <label className="flex items-start gap-2.5 text-[12px] leading-snug cursor-pointer" style={{ color: "#c9c3b6" }}>
                    <input type="checkbox" checked={form.privacy} onChange={set("privacy")} data-testid="oro-input-privacy" className="mt-0.5 w-4 h-4 shrink-0" />
                    <span>Ho letto e accetto l'<Link to="/privacy" className="underline" style={{ color: "#EDE7DB" }}>informativa privacy</Link> e acconsento a essere ricontattato.</span>
                  </label>
                  <button type="submit" disabled={loading} data-testid="oro-submit-button"
                    className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-[15px] transition-colors disabled:opacity-60"
                    style={{ background: "#6E1F2A", color: "#EDE7DB" }}>
                    {loading ? <><Loader2 size={17} className="animate-spin" /> Invio…</> : <>Voglio essere ricontattato</>}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-[11px] pt-1" style={{ color: "#8a857a" }}>
                    <Lock size={12} /> I tuoi dati restano riservati. Niente spam.
                  </p>
                </form>
              </>
            )}
          </div>
        </section>
      </main>

      {/* Footer + disclaimer */}
      <footer className="border-t" style={{ borderColor: "#161513" }}>
        <div className="max-w-5xl mx-auto px-5 py-8 text-center">
          <p className="text-[12px] leading-relaxed" style={{ color: "#6b675e" }}>
            Le informazioni presenti hanno scopo esclusivamente informativo e non costituiscono consulenza finanziaria,
            sollecitazione al pubblico risparmio né offerta di prodotti finanziari. L'oro è un bene reale il cui valore
            può variare nel tempo: rendimenti passati non garantiscono risultati futuri.
          </p>
          <p className="text-[12px] mt-3">
            <Link to="/privacy" className="underline" data-testid="oro-footer-privacy">Informativa privacy</Link>
            <span style={{ color: "#6b675e" }}> · </span>
            <Link to="/note-legali" className="underline" data-testid="oro-footer-note-legali">Termini e Note legali</Link>
            <span style={{ color: "#6b675e" }}> · Alex Gold · alexgold.it</span>
          </p>
        </div>
      </footer>

      {/* Modal cattura lead per la Guida gratuita */}
      {guideOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center px-4" style={{ background: "rgba(22,21,19,0.78)" }} data-testid="guide-modal-overlay" onClick={closeGuide}>
          <div className="w-full max-w-md rounded-2xl p-6 sm:p-7 shadow-2xl border relative" style={{ background: "#161513", color: "#EDE7DB", borderColor: "#3a3733", fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="guide-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={closeGuide} data-testid="guide-modal-close" className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full" style={{ background: "#211f1c", color: "#c9c3b6" }}>
              <X size={16} />
            </button>
            {guideDone ? (
              <div className="text-center py-3" data-testid="guide-modal-success">
                <p className="text-5xl mb-3">📘</p>
                <h3 className="font-bold text-2xl mb-2">La tua Guida è pronta!</h3>
                <p className="text-sm mb-5" style={{ color: "#c9c3b6" }}>Si è aperta in una nuova scheda. Ti ho inviato anche una copia via email 📩</p>
                <a href={GUIDA_ORO_PDF} target="_blank" rel="noopener noreferrer" data-testid="guide-modal-download"
                  className="block w-full rounded-full py-3.5 font-semibold text-[15px]" style={{ background: "#C9A227", color: "#161513" }}>
                  Scarica di nuovo la Guida
                </a>
                <button type="button" onClick={() => { closeGuide(); goToForm(); }} data-testid="guide-modal-to-form"
                  className="block w-full mt-3 text-sm underline" style={{ color: "#c9c3b6" }}>
                  Vuoi parlarne? Prenota una consulenza →
                </button>
              </div>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.25em] mb-3" style={{ color: "#C9A227" }}>Guida gratuita</p>
                <h3 className="font-bold text-2xl leading-tight mb-2">Oro fisico, spiegato senza fumo</h3>
                <p className="text-sm mb-5" style={{ color: "#c9c3b6" }}>Costi, premio, spread e come proteggere la liquidità ferma. Lascia i tuoi dati: te la mando subito.</p>
                <form onSubmit={submitGuide} className="space-y-3" data-testid="guide-modal-form">
                  <input value={guideForm.nome} onChange={setGuide("nome")} placeholder="Nome" data-testid="guide-input-nome" className={inputCls} style={inputStyle} />
                  <input value={guideForm.cognome} onChange={setGuide("cognome")} placeholder="Cognome" data-testid="guide-input-cognome" className={inputCls} style={inputStyle} />
                  <input value={guideForm.email} onChange={setGuide("email")} type="email" placeholder="Email" data-testid="guide-input-email" className={inputCls} style={inputStyle} />
                  <label className="flex items-start gap-2.5 text-[12px] leading-snug cursor-pointer" style={{ color: "#c9c3b6" }}>
                    <input type="checkbox" checked={guideForm.privacy} onChange={setGuide("privacy")} data-testid="guide-input-privacy" className="mt-0.5 w-4 h-4 shrink-0" />
                    <span>Ho letto e accetto l'<Link to="/privacy" className="underline" style={{ color: "#EDE7DB" }}>informativa privacy</Link>.</span>
                  </label>
                  <button type="submit" disabled={guideLoading} data-testid="guide-submit-button"
                    className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-[15px] transition-colors disabled:opacity-60"
                    style={{ background: "#C9A227", color: "#161513" }}>
                    {guideLoading ? <><Loader2 size={17} className="animate-spin" /> Invio…</> : <><FileText size={17} /> Ricevi la Guida gratuita</>}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-[11px] pt-1" style={{ color: "#8a857a" }}>
                    <Lock size={12} /> I tuoi dati restano riservati. Niente spam.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
